import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  MenuItem,
  Select,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import DashboardLayout from "layout";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Airport, getAvailableAirports } from "services/manualBooking";
import { useState, useEffect } from "react";
import { useToast } from "components/Toast";
import ReUseAbleTable, { Column } from "shared/ReUseAbleTable";
import {
  createPopularDestination,
  getPopularDestinations,
  PopularDestinationsResponse,
  deletePopularDestination,
  updatePopularDestination,
} from "../../services/popularDestinations";
import { format, parse } from "date-fns";

type PopularDestinationFormValues = {
  departure: Airport | null;
  destination: Airport | null;
  travelClass: string;
  date: Date | null;
  price: number;
  coverImage: File | null;
};

const schema = yup.object().shape({
  departure: yup.mixed<Airport>().required("Departure is required").nullable(),
  destination: yup
    .mixed<Airport>()
    .required("Destination is required")
    .nullable(),
  travelClass: yup.string().required("Class is required"),
  date: yup.date().required("Date is required").nullable(),
  price: yup
    .number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(0, "Price must be greater than or equal to 0"),
  coverImage: yup.mixed<File>().nullable(),
}) as yup.ObjectSchema<PopularDestinationFormValues>;

const inputLabelSx = {
  fontSize: "14px",
  color: "#344054",
  fontWeight: 600,
  mb: 1,
};

const textFieldSx = {
  bgcolor: "#F9F9F9",
  mt: 2,
  mb: 2,
  py: 1,
  px: 2,
  borderRadius: "10px",
  color: "#98A2B3",
  fontSize: "14px",
};

const PopularDestinations = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PopularDestinationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      departure: null,
      destination: null,
      travelClass: "Y",
      date: new Date(),
      price: 0,
      coverImage: null,
    },
  });

  const [departureSearch, setDepartureSearch] = useState("");
  const [destinationSearch, setDestinationSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);

  const [debouncedDepartureSearch, setDebouncedDepartureSearch] = useState("");
  const [debouncedDestinationSearch, setDebouncedDestinationSearch] =
    useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDepartureSearch(departureSearch);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [departureSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDestinationSearch(destinationSearch);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [destinationSearch]);

  const { data: departureAirports, isFetching: isDepartureFetching } = useQuery(
    {
      queryKey: ["airports", debouncedDepartureSearch],
      queryFn: () => getAvailableAirports(debouncedDepartureSearch),
      enabled: debouncedDepartureSearch.length > 1,
    },
  );

  const { data: destinationAirports, isFetching: isDestinationFetching } =
    useQuery({
      queryKey: ["airports", debouncedDestinationSearch],
      queryFn: () => getAvailableAirports(debouncedDestinationSearch),
      enabled: debouncedDestinationSearch.length > 1,
    });

  const departureOptions: Airport[] = departureAirports?.data?.airports || [];

  const destinationOptions: Airport[] =
    destinationAirports?.data?.airports || [];

  const { data: destinationsData, isLoading: isDestinationsLoading } =
    useQuery<PopularDestinationsResponse>({
      queryKey: ["popular-destinations"],
      queryFn: getPopularDestinations,
    });

  const destinations = destinationsData?.data?.items || [];

  const createMutation = useMutation({
    mutationFn: createPopularDestination,
    onSuccess: (response: any) => {
      showToast(
        response?.message || "Popular destination created successfully",
        "success",
      );
      queryClient.invalidateQueries({ queryKey: ["popular-destinations"] });
      reset({
        departure: null,
        destination: null,
        travelClass: "Y",
        date: new Date(),
        price: 0,
        coverImage: null,
      });
      setEditingId(null);
    },
    onError: (error: any) => {
      showToast(
        error?.response?.data?.message ||
          "Failed to create popular destination",
        "error",
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      updatePopularDestination(id, formData),
    onSuccess: (response: any) => {
      showToast(
        response?.message || "Popular destination updated successfully",
        "success",
      );
      queryClient.invalidateQueries({ queryKey: ["popular-destinations"] });
      reset({
        departure: null,
        destination: null,
        travelClass: "Y",
        date: new Date(),
        price: 0,
        coverImage: null,
      });
      setEditingId(null);
      setEditOpen(false);
    },
    onError: (error: any) => {
      showToast(
        error?.response?.data?.message ||
          "Failed to update popular destination",
        "error",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePopularDestination(id),
    onSuccess: (response: any) => {
      showToast(response?.message || "Deleted", "success");
      queryClient.invalidateQueries({ queryKey: ["popular-destinations"] });
      setDeleteOpen(false);
      setSelectedDeleteId(null);
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || "Failed to delete", "error");
    },
  });

  const onSubmit = (data: PopularDestinationFormValues) => {
    const formData = new FormData();
    const dep = data.departure as Airport;
    const des = data.destination as Airport;
    formData.append("departure", dep?.AirportCode || "");
    formData.append("departure_full", dep?.City || dep?.AirportName || "");
    formData.append("destination", des?.AirportCode || "");
    formData.append("destination_full", des?.City || des?.AirportName || "");
    formData.append("class", data.travelClass);
    if (data.date) formData.append("date", format(data.date, "MM/dd/yyyy"));
    formData.append("price", String(data.price));
    if (data.coverImage) formData.append("cover_image", data.coverImage);

    if (editingId) {
      updateMutation.mutate({ id: editingId, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  // Edit dialog form and submit
  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm<PopularDestinationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      departure: null,
      destination: null,
      travelClass: "Y",
      date: new Date(),
      price: 0,
      coverImage: null,
    },
  });

  const onEditSubmit = (data: PopularDestinationFormValues) => {
    if (!editingId) return;
    const formData = new FormData();
    const dep = data.departure as Airport;
    const des = data.destination as Airport;
    formData.append("departure", dep?.AirportCode || "");
    formData.append("departure_full", dep?.City || dep?.AirportName || "");
    formData.append("destination", des?.AirportCode || "");
    formData.append("destination_full", des?.City || des?.AirportName || "");
    formData.append("class", data.travelClass);
    if (data.date) formData.append("date", format(data.date, "MM/dd/yyyy"));
    formData.append("price", String(data.price));
    if (data.coverImage) formData.append("cover_image", data.coverImage);
    updateMutation.mutate({ id: editingId, formData });
  };

  const columns: Column[] = [
    { id: "departure", label: "Departure" },
    { id: "destination", label: "Destination" },
    { id: "date", label: "Date" },
    { id: "class", label: "Class" },
    { id: "price", label: "Price" },
    { id: "actions", label: "Actions", align: "right" },
  ];

  const classLabel = (c: string) =>
    c === "F"
      ? "First Class"
      : c === "C"
        ? "Business"
        : c === "W"
          ? "Premium Economy"
          : "Economy";

  const rows =
    destinations.map((item: any) => {
      const id = item._id || item.id;
      return {
        departure: item.departure_full || "",
        destination: item.destination_full || "",
        class: classLabel(item.class || "Y"),
        price: `₦${Number(item.price || 0).toLocaleString()}`,
        date: (() => {
          if (!item.date) return "";
          if (typeof item.date === "string" && item.date.includes("/")) {
            // Assume dd/MM/yyyy from backend
            try {
              const d = parse(item.date, "dd/MM/yyyy", new Date());
              return d.toLocaleDateString();
            } catch {
              return item.date;
            }
          }
          try {
            return new Date(item.date).toLocaleDateString();
          } catch {
            return String(item.date);
          }
        })(),
        actions: (
          <Box display="flex" gap={{ xs: 1, sm: 2 }} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingId(id || null);
                const depVal: Airport | null = item.departure
                  ? {
                      _id: "",
                      AirportCode: item.departure,
                      AirportName: item.departure_full || item.departure || "",
                      City: item.departure_full || "",
                      CityCountry: item.departure_full || "",
                      Country: "",
                    }
                  : null;
                const desVal: Airport | null = item.destination
                  ? {
                      _id: "",
                      AirportCode: item.destination,
                      AirportName:
                        item.destination_full || item.destination || "",
                      City: item.destination_full || "",
                      CityCountry: item.destination_full || "",
                      Country: "",
                    }
                  : null;
                resetEdit({
                  departure: depVal,
                  destination: desVal,
                  travelClass: item.class || "Y",
                  price: Number(item.price) || 0,
                  date: (() => {
                    if (!item.date) return new Date();
                    if (
                      typeof item.date === "string" &&
                      item.date.includes("/")
                    ) {
                      try {
                        return parse(item.date, "dd/MM/yyyy", new Date());
                      } catch {
                        return new Date();
                      }
                    }
                    const d = new Date(item.date);
                    return isNaN(d.getTime()) ? new Date() : d;
                  })(),
                  coverImage: null,
                });
                setEditOpen(true);
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => {
                setSelectedDeleteId(id || null);
                setDeleteOpen(true);
              }}
            >
              Delete
            </Button>
          </Box>
        ),
      };
    }) || [];

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Typography
          fontSize="24px"
          fontWeight="600"
          sx={{ mb: 1 }}
          color="#101828"
        >
          Add popular Destination
        </Typography>
        <Typography fontSize="14px" color="#475467" sx={{ mb: 4 }}>
          Enter Destination Details
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            bgcolor: "white",
            p: { xs: 2, sm: 4 },
            borderRadius: "12px",
            border: "1px solid #EAECF0",
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 3 },
            }}
          >
            <FormControl variant="standard" fullWidth>
              <InputLabel shrink sx={inputLabelSx}>
                Departure
              </InputLabel>
              <Controller
                name="departure"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={departureOptions}
                    getOptionLabel={(option) =>
                      `${option.AirportName} (${option.AirportCode})`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.AirportCode === value.AirportCode
                    }
                    filterOptions={(x) => x}
                    loading={isDepartureFetching}
                    loadingText="Searching airports..."
                    noOptionsText={
                      debouncedDepartureSearch.length > 1
                        ? "No airports found"
                        : "Type at least 2 characters"
                    }
                    value={field.value}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      // "reset" is MUI syncing the text to a selected option; searching
                      // that full label is a wasted request.
                      if (reason === "reset") return;
                      setDepartureSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Departure"
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        sx={textFieldSx}
                        error={!!errors.departure}
                        helperText={errors.departure?.message as string}
                      />
                    )}
                  />
                )}
              />
            </FormControl>

            <FormControl variant="standard" fullWidth>
              <InputLabel shrink sx={inputLabelSx}>
                Destination
              </InputLabel>
              <Controller
                name="destination"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={destinationOptions}
                    getOptionLabel={(option) =>
                      `${option.AirportName} (${option.AirportCode})`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.AirportCode === value.AirportCode
                    }
                    filterOptions={(x) => x}
                    loading={isDestinationFetching}
                    loadingText="Searching airports..."
                    noOptionsText={
                      debouncedDestinationSearch.length > 1
                        ? "No airports found"
                        : "Type at least 2 characters"
                    }
                    value={field.value}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      // "reset" is MUI syncing the text to a selected option; searching
                      // that full label is a wasted request.
                      if (reason === "reset") return;
                      setDestinationSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Destination"
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        sx={textFieldSx}
                        error={!!errors.destination}
                        helperText={errors.destination?.message as string}
                      />
                    )}
                  />
                )}
              />
            </FormControl>

            <FormControl variant="standard" fullWidth>
              <InputLabel shrink sx={inputLabelSx}>
                Class
              </InputLabel>
              <Controller
                name="travelClass"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    variant="standard"
                    sx={{
                      ...textFieldSx,
                      border: "none",
                      boxShadow: "none",
                      "& fieldset": { border: "none" },
                    }}
                    error={!!errors.travelClass}
                  >
                    <MenuItem value="F">First Class</MenuItem>
                    <MenuItem value="C">Business Class</MenuItem>
                    <MenuItem value="W">Premium Economy</MenuItem>
                    <MenuItem value="Y">Economy</MenuItem>
                  </Select>
                )}
              />
            </FormControl>

            <FormControl variant="standard" fullWidth>
              <InputLabel shrink sx={inputLabelSx}>
                Date
              </InputLabel>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          InputProps: { disableUnderline: true },
                          sx: textFieldSx,
                          error: !!errors.date,
                          helperText: errors.date?.message as string,
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </FormControl>

            <FormControl variant="standard" fullWidth>
              <InputLabel shrink sx={inputLabelSx}>
                Price
              </InputLabel>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="# 100,000"
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={textFieldSx}
                    error={!!errors.price}
                    helperText={errors.price?.message as string}
                  />
                )}
              />
            </FormControl>

            <FormControl variant="standard" fullWidth>
              <InputLabel shrink sx={inputLabelSx}>
                Cover image
              </InputLabel>
              <Controller
                name="coverImage"
                control={control}
                render={({ field }) => (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        ...textFieldSx,
                        justifyContent: "flex-start",
                        color: field.value ? "#101828" : "#98A2B3",
                        border: "1px dashed #D0D5DD",
                        bgcolor: "#F9F9F9",
                      }}
                    >
                      {field.value ? field.value.name : "Select Image"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          field.onChange(file);
                        }}
                      />
                    </Button>
                    <Typography fontSize="12px" color="#98A2B3" sx={{ mt: 1 }}>
                      Please use only JPG, PNG image file.
                    </Typography>
                  </Box>
                )}
              />
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="center" mt={4}>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
              sx={{
                bgcolor: "primary.main",
                px: 8,
                py: 1.5,
                borderRadius: "999px",
                "&:hover": {
                  bgcolor: "primary.main",
                },
              }}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : editingId
                  ? "Update"
                  : "Save"}
            </Button>
            {editingId && (
              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={() => {
                  reset({
                    departure: null,
                    destination: null,
                    travelClass: "Y",
                    date: new Date(),
                    price: 0,
                    coverImage: null,
                  });
                  setEditingId(null);
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: "white",
            p: { xs: 2, sm: 4 },
            borderRadius: "12px",
            border: "1px solid #EAECF0",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography fontSize="16px" fontWeight={600} color="#101828">
              Destination Bookings History
            </Typography>
          </Box>

          <ReUseAbleTable columns={columns} rows={rows} />
        </Box>
        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          fullWidth
          fullScreen={isMobile}
          maxWidth="lg"
        >
          <DialogTitle>Edit Popular Destination</DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 2, sm: 3 },
                mt: 1,
              }}
            >
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink sx={inputLabelSx}>
                  Departure
                </InputLabel>
                <Controller
                  name="departure"
                  control={editControl}
                  render={({ field }) => (
                    <Autocomplete
                      options={departureOptions}
                      getOptionLabel={(option) =>
                        `${option.AirportName} (${option.AirportCode})`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.AirportCode === value.AirportCode
                      }
                      filterOptions={(x) => x}
                      loading={isDepartureFetching}
                      loadingText="Searching airports..."
                      noOptionsText={
                        debouncedDepartureSearch.length > 1
                          ? "No airports found"
                          : "Type at least 2 characters"
                      }
                      value={field.value}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        // "reset" is MUI syncing the text to a selected option; searching
                        // that full label is a wasted request.
                        if (reason === "reset") return;
                        setDepartureSearch(newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Departure"
                          variant="standard"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                          }}
                          sx={textFieldSx}
                          error={!!editErrors.departure}
                          helperText={editErrors.departure?.message as string}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>

              <FormControl variant="standard" fullWidth>
                <InputLabel shrink sx={inputLabelSx}>
                  Destination
                </InputLabel>
                <Controller
                  name="destination"
                  control={editControl}
                  render={({ field }) => (
                    <Autocomplete
                      options={destinationOptions}
                      getOptionLabel={(option) =>
                        `${option.AirportName} (${option.AirportCode})`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.AirportCode === value.AirportCode
                      }
                      filterOptions={(x) => x}
                      loading={isDestinationFetching}
                      loadingText="Searching airports..."
                      noOptionsText={
                        debouncedDestinationSearch.length > 1
                          ? "No airports found"
                          : "Type at least 2 characters"
                      }
                      value={field.value}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        // "reset" is MUI syncing the text to a selected option; searching
                        // that full label is a wasted request.
                        if (reason === "reset") return;
                        setDestinationSearch(newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Destination"
                          variant="standard"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                          }}
                          sx={textFieldSx}
                          error={!!editErrors.destination}
                          helperText={editErrors.destination?.message as string}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>

              <FormControl variant="standard" fullWidth>
                <InputLabel shrink sx={inputLabelSx}>
                  Class
                </InputLabel>
                <Controller
                  name="travelClass"
                  control={editControl}
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      variant="standard"
                      sx={{
                        ...textFieldSx,
                        border: "none",
                        boxShadow: "none",
                        "& fieldset": { border: "none" },
                      }}
                      error={!!editErrors.travelClass}
                    >
                      <MenuItem value="F">First Class</MenuItem>
                      <MenuItem value="C">Business Class</MenuItem>
                      <MenuItem value="W">Premium Economy</MenuItem>
                      <MenuItem value="Y">Economy</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl variant="standard" fullWidth>
                <InputLabel shrink sx={inputLabelSx}>
                  Date
                </InputLabel>
                <Controller
                  name="date"
                  control={editControl}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        slotProps={{
                          textField: {
                            variant: "standard",
                            InputProps: { disableUnderline: true },
                            sx: textFieldSx,
                            error: !!editErrors.date,
                            helperText: editErrors.date?.message as string,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>

              <FormControl variant="standard" fullWidth>
                <InputLabel shrink sx={inputLabelSx}>
                  Price
                </InputLabel>
                <Controller
                  name="price"
                  control={editControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="# 100,000"
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      sx={textFieldSx}
                      error={!!editErrors.price}
                      helperText={editErrors.price?.message as string}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleEditSubmit(onEditSubmit)}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Updating..." : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>Delete Destination</DialogTitle>
          <DialogContent>
            <Typography fontSize="14px" color="#475467">
              Are you sure you want to delete this popular destination?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() =>
                selectedDeleteId && deleteMutation.mutate(selectedDeleteId)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default PopularDestinations;
